// src/pages/Accounts/IncomeList.js
import React, { useEffect, useState } from "react";

import { Table, Button, Row, Col, Typography, App, Tooltip, Drawer, Grid} from "antd";
import { ArrowLeftOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchIncomes, deleteIncome } from "./incomeThunk";
import {
  resetIncomes,
  setSelectedIncome,
  setIsEditIncome,
  setRefreshList,
} from "./incomeSlice";

import commaNumber from "comma-number";
import { formatDateOnly, getAccessType } from "../../lib/functions";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";

const format = commaNumber.bindWith(",", ".");

const { Title } = Typography;

const IncomeList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, loading, refreshList } = useSelector((state) => state.incomes);
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [helpOpen, setHelpOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isAdminUser = useIsAdminUser();

  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;


  useEffect(() => {
    const access = getAccessType("income_entry");
    access_type_set(access);
  }, []);
  useEffect(() => {
    dispatch(resetIncomes());
    dispatch(fetchIncomes());
  }, [dispatch]);

  useEffect(() => {
    if (refreshList) {
      dispatch(fetchIncomes());
      dispatch(setRefreshList(false));
    }
  }, [refreshList, dispatch]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const handleDelete = async (inc_id) => {
    try {
      await dispatch(deleteIncome(inc_id)).unwrap();
      modal.success({
        title: "Income entry deleted successfully",
      });
      dispatch(fetchIncomes());
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Income Code",
      dataIndex: "inc_code",
    },
    {
      title: "Income Date",
      dataIndex: "created_date",
      render: (value) => (value ? formatDateOnly(value) : "-"),
    },
    {
      title: "Received Into",
      dataIndex: "acc_name",
      render: (val, record) => val || record.from_acc_name || "",
    },

    {
      title: "Income Total",
      dataIndex: "inc_total",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Description",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
  {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit) || txLocked}
            icon={<EditOutlined />}
            onClick={() => {
              if (txLocked) return;

              dispatch(setSelectedIncome(record));
              dispatch(setIsEditIncome(true));

              navigate(`/accounts/income/edit/${record.inc_id}`);
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            disabled={!(isAdminUser || canDelete) || txLocked}
            icon={<DeleteOutlined />}
            onClick={() => {
              if (txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete Income entry with amount: ${record.inc_total}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.inc_id),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
  ];

  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Inconme Head", dataIndex: "acc_name" },
    {
      title: "Amount",
      dataIndex: "inc_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="income-list-back-button"
        />
        <Title level={3} style={{ margin: 0 }}>
          Incomes List
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      </div>
      <Row justify="end" style={{ marginBottom: 32, marginTop: 32 }}>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedIncome(null));
                  dispatch(setIsEditIncome(false));
                  navigate("/accounts/income/add");
                }}
                data-testid="btn-add-income"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={list}
        rowKey="inc_id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        size="middle"
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <Table
                rowKey="detailId"
                columns={detailColumns}
                dataSource={record.details || []}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: (record.details || []).length,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (page) => {
                    setCurrentPage(page);
                  },
                  onShowSizeChange: (_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  },
                }}
                size="small"
                bordered
              />
            );
          },
        }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
      <Drawer
        title="Income Entry Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Income Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Income Entry module is used to record and manage company income
            transactions within the ERP accounting system. It helps track received
            amounts, income heads, account movements, and financial reporting.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View income transaction records</li>
              <li>Use <strong>Add New</strong> to create income entries</li>
              <li>Edit income details when required</li>
              <li>Delete income entries if permitted</li>
              <li>Track received account information</li>
              <li>Monitor income totals and narration details</li>
              <li>Review detailed income breakdowns using expandable rows</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Income entries affect accounting balances immediately.</li>
              <li>Income heads help organize financial reporting.</li>
              <li>Expandable rows display detailed income allocations.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for auditing and reports.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default IncomeList;
